import { trigger, transition, style, animate } from '@angular/animations';
import { Component, Input, OnInit, QueryList, ViewChild, ViewChildren, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { SharedService } from '@shared/services/shared.service';
import { IInHomeCareLicense } from 'app/models/licenses/in-home-care/in-home-care-license';
import { InHomeCareLicenseService } from 'app/services/license/in-home-care/in-home-care-license.service';
import { AdministratorApplicationComponent } from '../administrator-application/administrator-application.component';
import { IhcParentValidator } from '@shared/validators/ihc-parent-validator';
import { IhcNameValidator } from '@shared/validators/ihc-name-validator';
import { AddressComponent } from 'app/components/reusable/address/address.component';
import { BranchOperationComponent } from 'app/components/reusable/branch-operation/branch-operation.component';
import { DocumentComponent } from 'app/components/reusable/document/document.component';
import { EntityOwnerComponent } from 'app/components/reusable/entity-owner/entity-owner.component';
import { OfficeHoursComponent } from 'app/components/reusable/office-hours/office-hours.component';
import { ISearch } from 'app/models/licenses/search';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { IStaff } from 'app/models/user/staff';
import { AuthService } from '@shared/auth/auth.service';
import { MatSelectChange } from '@angular/material/select';
import { ILicenseChangeDialog } from '../in-home-care-change/in-home-care-change-dialog.component';
import { IAddress } from 'app/models/address/address';
import { ExportInHomeCareLicenseDialogComponent } from '../dialog-export-in-home-care-license/dialog-export-in-home-care-license.component';
import { ManagePaymentComponent } from 'app/components/payments/components/manage/manage-payments.component';
import { IPayment } from 'app/components/payments/models/payment';

@Component({
  providers: [IhcNameValidator],
  selector: 'app-in-home-care',
  templateUrl: './in-home-care.component.html',
  styleUrls: ['./in-home-care.component.scss'],
  animations: [
    trigger('slideLeft', [
      transition(':enter', [
        style({ transform: 'translateX(-100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(-100%)' }))
      ])
    ]),
    trigger('slideRight', [
      transition(':enter', [
        style({ transform: 'translateX(100%)' }),
        animate('200ms ease-in', style({ transform: 'translateX(0%)' }))
      ]),
      transition(':leave', [
        animate('200ms ease-in', style({ transform: 'translateX(100%)' }))
      ])
    ])
  ]
})

export class InHomeCareComponent implements OnInit {
  @ViewChildren(DocumentComponent) documents!: QueryList<DocumentComponent>;
  @ViewChild(AddressComponent) addresses!: AddressComponent;
  @ViewChild(OfficeHoursComponent) officeHours!: OfficeHoursComponent;
  @ViewChild(EntityOwnerComponent) owners!: EntityOwnerComponent;
  @ViewChild(BranchOperationComponent) branchOperations!: BranchOperationComponent;
  @ViewChild(AdministratorApplicationComponent) adminApplication!: AdministratorApplicationComponent;
  @ViewChild(ManagePaymentComponent) payments!: ManagePaymentComponent;
  @Input() assigned: boolean = false;

  public id: number = 0;
  public ihc: string = "IHC";
  public renewal: boolean = false;
  public deleteId: number = 0;
  public editing: boolean = false;
  public subunitLoaded: boolean = false;
  public addressesLoaded: boolean = false;
  public parentId: number = 0;
  public paymentsLoaded: boolean = false;
  public inHomeCareTypes: string[] = [
    'Proof of Business Zoning Approval or Allowance',
    'IHC Policies, Procedures and Forms',
    'Survey Documents',
    'Policy and Procedure Checklist',
    'Other'
  ];
  public search: ISearch = {
    searchTerm: '',
    entityGuid: false,
    entityName: false,
    assignedTo: '',
    includeArchived: false,
    statuses: []
  }
  public validationErrors: string[] = [];
  public licenses: IInHomeCareLicense[] = [];
  public license: IInHomeCareLicense = {
    id: 0,
    userId: '',
    licenseId: '',
    enteredByStaff: false,
    assignedToId: '',
    assignedToName: '',
    finalApprovalBy: '',
    canRenew: false,
    isRenewal: false,
    electronicSignature: '',
    signedDate: '',
    applicationDate: '',
    approvedDate: '',
    deniedDate: '',
    expirationDate: '',
    entityName: '',
    entityWebsite: '',
    businessInOregon: false,
    secretaryOfStateNumber: '',
    registeredBusiness: false,
    registeredIn: '',
    registryNumber: '',
    isSubunit: false,
    parentAgency: '',
    parentGuid: '',
    parentCity: '',
    calculatedDistance: 0,
    agencyCity: '',
    agencyDBAName: '',
    agencyPhone: '',
    agencyEmail: '',
    agencyFax: '',
    emergencyContactName: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    agencyPhysicalLocation: '',
    otherLocation: '',
    classificationLevel: '',
    adminDesigneeName: '',
    adminDesigneeTitle: '',
    qualifiedEntityName: '',
    qualifiedEntityTitle: '',
    registeredNurseName: '',
    adminPrintName: '',
    adminPrintTitle: '',
    initialLicensure: '',
    staffInitials: '',
    initialSurveyComplete: false,
    primaryApproverName: '',
    primaryApproved: false,
    primaryInitials: '',
    primaryApprovalDate: '',
    secondaryApproverName: '',
    secondaryApproved: false,
    secondaryInitials: '',
    secondaryApprovalDate: '',
    reason: '',
    backgroundCheckCompleted: false,
    backgroundCheckDate: '',
    renewalVerification: '',
    archived: false,
    statuses: []
  }
  public staff: IStaff[] = [];

  // Pagination state properties
  private currentPageIndex: number = 0;
  private currentPageSize: number = 50;

  public parentValidator = inject(IhcParentValidator);
  public nameValidator = inject(IhcNameValidator);

  public licenseForm = new FormGroup({
    classificationLevel: new FormControl('', Validators.required),
    isSubunit: new FormControl(false),
    parentAgency: new FormControl('', {
      asyncValidators: [this.parentValidator.validate.bind(this.parentValidator)],
      updateOn: 'blur'
    }),
    agencyDBAName: new FormControl('', Validators.required),
    agencyPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    agencyEmail: new FormControl('', [Validators.required, Validators.email]),
    agencyFax: new FormControl('', [Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyContactName: new FormControl('', Validators.required),
    emergencyContactPhone: new FormControl('', [Validators.required, Validators.pattern(/^\(?([0-9]{3})\)?[-]?([0-9]{3})[-]?([0-9]{4}).*$/)]),
    emergencyContactEmail: new FormControl('', Validators.required),
    agencyPhysicalLocation: new FormControl('', Validators.required),
    otherLocation: new FormControl(''),
    adminDesigneeName: new FormControl('', Validators.required),
    adminDesigneeTitle: new FormControl('', Validators.required),
    qualifiedEntityName: new FormControl('', Validators.required),
    qualifiedEntityTitle: new FormControl('', Validators.required),
    registeredNurseName: new FormControl(''),
    initialLicensure: new FormControl(''),
    staffInitials: new FormControl(''),
    entityName: new FormControl('', {
      asyncValidators: [this.nameValidator.validate.bind(this.nameValidator)],
      updateOn: 'blur'
    }),
    entityWebsite: new FormControl('', Validators.pattern(/^(http:\/\/|https:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]/i)),
    businessInOregon: new FormControl(false),
    secretaryOfStateNumber: new FormControl(''),
    registeredBusiness: new FormControl(false),
    registeredIn: new FormControl(''),
    registryNumber: new FormControl(''),
    calculatedDistance: new FormControl(0),
    electronicSignature: new FormControl('', Validators.required),
    signedDate: new FormControl<Date | null>(null, Validators.required),
    adminPrintTitle: new FormControl('', Validators.required),
    initialSurveyComplete: new FormControl(false),
    primaryApproverName: new FormControl(''),
    primaryApproved: new FormControl<boolean | null>(null),
    primaryInitials: new FormControl(''),
    primaryApprovalDate: new FormControl<Date | null>(null),
    secondaryApproverName: new FormControl(''),
    secondaryApproved: new FormControl<boolean | null>(null),
    secondaryInitials: new FormControl(''),
    secondaryApprovalDate: new FormControl<Date | null>(null),
    backgroundCheckCompleted: new FormControl(false),
    backgroundCheckDate: new FormControl<Date | null>(null),
    approvedDate: new FormControl<Date | null>(null),
    expirationDate: new FormControl<Date | null>(null),
    status: new FormControl(0),
    reason: new FormControl(''),
  }, {
    validators: [
      this.physicalLocationValidator(),
      this.registeredNurseValidator()
    ]
  });

  public searchForm = new FormGroup({
    searchTerm: new FormControl(''),
    entityGuid: new FormControl(false),
    entityName: new FormControl(false),
    assignedTo: new FormControl(''),
    includeArchived: new FormControl(false),
    statuses: new FormControl(['Approved'])
  });

  public licenseColumns: string[] = ['licenseId', 'expirationDate', 'entityName', 'parentCity',
                                     'statuses', 'isSubunit', 'agencyPhone', 'agencyEmail', 'assignedToName'];
  public licenseSource = new MatTableDataSource<IInHomeCareLicense>(this.licenses);
  @ViewChild('licensePaginator', { static: false }) licensePaginator!: MatPaginator;
  @ViewChild('licenseSort', { static: false }) licenseSort!: MatSort;

  public sharedService = inject(SharedService);
  public licenseService = inject(InHomeCareLicenseService);
  public authService = inject(AuthService);
  public router = inject(Router);
  public route = inject(ActivatedRoute);
  public dialog = inject(MatDialog);

  constructor() {
    this.renewal = this.router.url.includes('renewal');
    this.id = Number(this.route.snapshot.paramMap.get('id'));
  }

  ngOnInit(): void {
    if (this.renewal) {
      this.searchForm.get('statuses')?.setValue(['Renewal']);
    }

    this.assigned ? this.getAssignedLicenses() : this.searchLicenses();
    this.getStaff();
    this.sharedService.getUserTheme();
    this.sharedService.getStatuses();
  }

  private savePaginationState(): void {
    if (this.licensePaginator) {
      this.currentPageIndex = this.licensePaginator.pageIndex;
      this.currentPageSize = this.licensePaginator.pageSize;
    }
  }

  private restorePaginationState(): void {
    if (this.licensePaginator) {
      this.licensePaginator.pageIndex = this.currentPageIndex;
      this.licensePaginator.pageSize = this.currentPageSize;
      this.licenseSource.paginator = this.licensePaginator;
    }
  }

  onPaginationChange(): void {
    this.savePaginationState();
  }

  getAssignedLicenses(): void {
    this.licenseService.getAssignedLicenses().subscribe({
      next: response => {
        this.licenses = response;
      },
      error: error => {
        console.log('error getting assigned licenses', error);
        this.sharedService.toastr.error('An error occurred while getting assigned licenses.');
      },
      complete: () => {
        this.updateLicenseTable();
      }
    });
  }

  searchLicenses(): void {
    const form = this.searchForm.getRawValue();
    this.search.searchTerm = form.searchTerm;
    this.search.entityGuid = form.entityGuid;
    this.search.entityName = form.entityName;
    this.search.assignedTo = form.assignedTo;
    this.search.includeArchived = form.includeArchived;
    this.search.statuses = form.statuses || [];

    this.licenseService.searchLicenses(this.search).subscribe({
      next: response => {
        this.licenses = response;
      },
      error: error => {
        console.log('error searching for licenses', error);
        this.sharedService.toastr.error('An error occurred while searching for licenses.');
      },
      complete: () => {
        this.updateLicenseTable();
      }
    });
  }

  getStaff(): void {
    this.licenseService.getStaff(this.ihc).subscribe({
      next: response => {
        this.staff = response;
      },
      error: error => {
        console.log('error getting staff', error);
        this.sharedService.toastr.error('An error occurred while getting staff.');
      }
    });
  }

  updateLicenseTable(): void {
    setTimeout(() => {
      this.licenseSource.data = this.licenses;
      // Sort agency names by DBA, expiration dates chronologically, and statuses by their first label.
      this.licenseSource.sortingDataAccessor = (data: IInHomeCareLicense, sortHeaderId: string) => {
        switch (sortHeaderId) {
          case 'entityName':
            return data.agencyDBAName || '';
          case 'expirationDate': {
            const timestamp = data.expirationDate ? Date.parse(data.expirationDate) : NaN;
            return isNaN(timestamp) ? 0 : timestamp;
          }
          case 'statuses':
            return data.statuses && data.statuses.length > 0 ? data.statuses[0].label : '';
          default:
            return (data as any)[sortHeaderId];
        }
      };
      this.licenseSource.sort = this.licenseSort;
      // Restore pagination state after table update
      this.restorePaginationState();
    }, 500);
  }

  selectLicense(license: IInHomeCareLicense): void {
    // Save current pagination state before selecting license
    this.savePaginationState();

    this.addressesLoaded = false;
    this.subunitLoaded = false;
    this.parentId = 0;
    this.license = license;
    this.updateLicenseForm(license);
    if (this.license.parentAgency) {
      this.getParentInfo(false);
    }

    this.licenseForm.markAllAsTouched();

    // Run validation for the selected license
    setTimeout(() => {
      this.validateApplication();
    }, 1500);
  }

  toggleEditing(editing: boolean): void {
    this.editing = editing;
  }

  updateLicense(): void {
    const form = this.licenseForm.getRawValue();
    this.license.classificationLevel = form.classificationLevel;
    this.license.isSubunit = form.isSubunit;
    this.license.parentAgency = form.parentAgency;
    this.license.agencyDBAName = form.agencyDBAName;
    this.license.agencyPhone = form.agencyPhone;
    this.license.agencyEmail = form.agencyEmail;
    this.license.agencyFax = form.agencyFax;
    this.license.emergencyContactName = form.emergencyContactName;
    this.license.emergencyContactPhone = form.emergencyContactPhone;
    this.license.emergencyContactEmail = form.emergencyContactEmail;
    this.license.agencyPhysicalLocation = form.agencyPhysicalLocation;
    this.license.otherLocation = form.otherLocation;
    this.license.adminDesigneeName = form.adminDesigneeName;
    this.license.adminDesigneeTitle = form.adminDesigneeTitle;
    this.license.qualifiedEntityName = form.qualifiedEntityName;
    this.license.qualifiedEntityTitle = form.qualifiedEntityTitle;
    this.license.registeredNurseName = form.registeredNurseName;
    this.license.initialLicensure = form.initialLicensure;
    this.license.staffInitials = form.staffInitials;
    this.license.entityName = form.entityName;
    this.license.entityWebsite = form.entityWebsite;
    this.license.businessInOregon = form.businessInOregon;
    this.license.secretaryOfStateNumber = form.secretaryOfStateNumber;
    this.license.registeredBusiness = form.registeredBusiness;
    this.license.registeredIn = form.registeredIn;
    this.license.registryNumber = form.registryNumber;
    this.license.calculatedDistance = form.calculatedDistance;
    this.license.electronicSignature = form.electronicSignature;
    this.license.signedDate = form.signedDate?.toDateString()!;
    this.license.approvedDate = form.approvedDate?.toDateString()!;
    this.license.expirationDate = form.expirationDate?.toDateString()!;
    this.license.adminPrintTitle = form.adminPrintTitle;
    this.license.initialSurveyComplete = form.initialSurveyComplete;
    this.license.primaryApproverName = form.primaryApproverName;
    this.license.primaryApproved = form.primaryApproved;
    this.license.primaryInitials = form.primaryInitials;
    this.license.primaryApprovalDate = form.primaryApprovalDate?.toDateString()!;
    this.license.secondaryApproverName = form.secondaryApproverName;
    this.license.secondaryApproved = form.secondaryApproved;
    this.license.secondaryInitials = form.secondaryInitials;
    this.license.secondaryApprovalDate = form.secondaryApprovalDate?.toDateString()!;
    this.license.backgroundCheckDate = form.backgroundCheckDate?.toDateString()!;
    this.license.backgroundCheckCompleted = form.backgroundCheckCompleted;
    this.license.reason = form.reason;
  }

  updateLicenseForm(license: IInHomeCareLicense): void {
    this.nameValidator.licenseId = license.licenseId ?? '';
    this.licenseForm.reset({
      classificationLevel: license.classificationLevel,
      isSubunit: license.isSubunit,
      parentAgency: license.parentAgency,
      agencyDBAName: license.agencyDBAName,
      agencyPhone: license.agencyPhone,
      agencyEmail: license.agencyEmail,
      agencyFax: license.agencyFax,
      emergencyContactName: license.emergencyContactName,
      emergencyContactPhone: license.emergencyContactPhone,
      emergencyContactEmail: license.emergencyContactEmail,
      agencyPhysicalLocation: license.agencyPhysicalLocation,
      otherLocation: license.otherLocation,
      adminDesigneeName: license.adminDesigneeName,
      adminDesigneeTitle: license.adminDesigneeTitle,
      qualifiedEntityName: license.qualifiedEntityName,
      qualifiedEntityTitle: license.qualifiedEntityTitle,
      registeredNurseName: license.registeredNurseName,
      initialLicensure: license.initialLicensure,
      staffInitials: license.staffInitials,
      entityName: license.entityName,
      entityWebsite: license.entityWebsite,
      businessInOregon: license.businessInOregon,
      secretaryOfStateNumber: license.secretaryOfStateNumber,
      registeredBusiness: license.registeredBusiness,
      registeredIn: license.registeredIn,
      registryNumber: license.registryNumber,
      calculatedDistance: license.calculatedDistance,
      electronicSignature: license.electronicSignature,
      adminPrintTitle: license.adminPrintTitle,
      initialSurveyComplete: license.initialSurveyComplete,
      primaryApproverName: license.primaryApproverName,
      primaryApproved: license.primaryApproved,
      primaryInitials: license.primaryInitials,
      secondaryApproverName: license.secondaryApproverName,
      secondaryApproved: license.secondaryApproved,
      secondaryInitials: license.secondaryInitials,
      backgroundCheckCompleted: license.backgroundCheckCompleted,
      reason: license.reason,
    });

    const dateFields = ['signedDate', 'approvedDate', 'primaryApprovalDate', 'secondaryApprovalDate',
                        'lastFingerprintsDate', 'backgroundCheckDate', 'expirationDate'];
    dateFields.forEach(field => {
      const value = (license as any)[field];
      if (value) {
        const control = this.licenseForm.get(field);
        if (control) {
          control.setValue(new Date(value));
        }
      }
    });
  }

  getEmptyLicense(): IInHomeCareLicense {
    return {
      id: 0,
      userId: '',
      licenseId: '',
      enteredByStaff: false,
      assignedToId: '',
      assignedToName: '',
      finalApprovalBy: '',
      canRenew: false,
      isRenewal: false,
      electronicSignature: '',
      signedDate: '',
      applicationDate: '',
      approvedDate: '',
      deniedDate: '',
      expirationDate: '',
      entityName: '',
      entityWebsite: '',
      businessInOregon: false,
      secretaryOfStateNumber: '',
      registeredBusiness: false,
      registeredIn: '',
      registryNumber: '',
      isSubunit: false,
      parentGuid: '',
      parentAgency: '',
      parentCity: '',
      calculatedDistance: 0,
      agencyCity: '',
      agencyDBAName: '',
      agencyPhone: '',
      agencyEmail: '',
      agencyFax: '',
      emergencyContactName: '',
      emergencyContactPhone: '',
      emergencyContactEmail: '',
      agencyPhysicalLocation: '',
      otherLocation: '',
      classificationLevel: '',
      adminDesigneeName: '',
      adminDesigneeTitle: '',
      qualifiedEntityName: '',
      qualifiedEntityTitle: '',
      registeredNurseName: '',
      adminPrintName: '',
      adminPrintTitle: '',
      initialLicensure: '',
      staffInitials: '',
      initialSurveyComplete: false,
      primaryApproverName: '',
      primaryApproved: null,
      primaryInitials: '',
      primaryApprovalDate: '',
      secondaryApproverName: '',
      secondaryApproved: null,
      secondaryInitials: '',
      secondaryApprovalDate: '',
      reason: '',
      archived: false,
      backgroundCheckCompleted: false,
      backgroundCheckDate: '',
      renewalVerification: '',
      statuses: []
    }
  }

  closeLicense(): void {
    this.license = this.getEmptyLicense();
    this.editing = false;
    this.subunitLoaded = false;
    this.addressesLoaded = false;
    this.parentId = 0;
    this.paymentsLoaded = false;
    this.validationErrors = []; // Clear validation errors when closing license
    this.updateLicenseForm(this.license);
    this.updateLicenseTable();
  }

  createLicense(): void {
    this.sharedService.openConfirm('Create a new license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.licenseService.createLicense().subscribe({
            next: response => {
              this.editing = true;
              this.license = response;
              this.updateLicenseForm(response);
            },
            error: error => {
              console.log('error creating license', error);
              this.sharedService.toastr.error('An error occurred while creating the license.');
            },
            complete: () => {
              this.licenses.push(this.license);
              this.sharedService.toastr.success('License created successfully.');
            }
          });
        }
      }
    );
  }

  saveLicense(): void {
    this.sharedService.openConfirm('Update this license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.updateLicense();
          this.licenseService.updateLicense(this.license).subscribe({
            complete: () => {
              this.officeHours.saveOfficeHours();
              this.adminApplication.saveApplication();
              const newStatus = this.licenseForm.get('status')?.value;
              if (newStatus !== null && !this.license.statuses.some(s => s.id === newStatus)) {
                this.updateLicenseStatus(newStatus!);
              }
              else {
                this.sharedService.toastr.success('License updated successfully.');
              }
            },
            error: error => {
              console.log('error updating license', error);
              this.sharedService.toastr.error('An error occurred while updating the license.');
            }
          });
        }
      }
    );
  }

  approveLicense(): void {
    this.sharedService.openConfirm('Approve this license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          const statusId = this.sharedService.statusList.find(s => s.label === this.sharedService.approved)?.id;
          this.updateLicense();
          this.licenseService.updateLicense(this.license).subscribe({
            complete: () => {
              this.officeHours.saveOfficeHours();
              this.adminApplication.saveApplication();
              this.updateLicenseStatus(statusId!);
            },
            error: error => {
              console.log('error updating license', error);
              this.sharedService.toastr.error('An error occurred while updating the license.');
            }
          });
        }
      }
    );
  }

  updateLicenseStatus(statusId: number): void {
    this.licenseService.updateLicenseStatus(this.license.id, statusId).subscribe({
      next: response => {
        this.license = response;
        this.updateLicenseForm(this.license);
      },
      error: error => {
        console.log('error updating license status', error);
        this.sharedService.toastr.error('An error occurred while updating the license status.');
      },
      complete: () => {
        this.sharedService.toastr.success('License updated successfully.');
        let index: number = this.licenses.findIndex(l => l.id === this.license.id);
        this.licenses[index] = this.license;
      }
    });
  }

  archiveLicense(license: IInHomeCareLicense, archive: boolean): void {
    let archiving: string = archive ? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this license?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if (confirmed) {
          this.licenseService.archiveLicense(license.id).subscribe({
            complete: () => {
              license.archived = archive;
              if (archive) {
                this.licenses = this.licenses.filter(o => o.id !== license.id);
                this.closeLicense();
              }
            },
            error: e => {
              this.sharedService.toastr.error("Could Not Archive License");
              console.log('error archiving license', e);
            }
          });
        }
      }
    );
  }

  assignLicense(event: MatSelectChange, licenseId: number): void {
    let user: IStaff = this.staff.find(staff => staff.name === event.value)!;
    if (user === undefined) {
      let emptyUser: IStaff = {
        id: '',
        name: '',
        email: ''
      }
      user = emptyUser;
    }
    this.licenseService.assignLicense(licenseId, user).subscribe({
      complete: () => {
        this.licenses.find(tp => tp.id == licenseId)!.assignedToName = user.name;
        if (user === null || user.name === null || user.name.length === 0) {
          this.sharedService.toastr.success("License unassigned");
        }
        else {
          this.sharedService.toastr.success("License assigned to " + user.name);
        }
      },
      error: error => {
        console.log('error assigning license', error);
        this.sharedService.toastr.error('An error occurred while assigning the license.');
      }
    });
  }

  requestChange(entityId: number, entityType: string, type: string) {
    let dialogData: ILicenseChangeDialog = {
      licenseType: this.sharedService.inHomeCareLicense,
      licenseId: this.license.id,
      licenseGuid: this.license.licenseId,
      isSubunit: this.license.isSubunit!,
      entityId: entityId,
      entityType: entityType,
      originalValue: '',
      newValue: '',
      changeAction: type
    }

    if (entityType === this.sharedService.classification) {
      dialogData.originalValue = this.license.classificationLevel;
    }
    else if (entityType === this.sharedService.nurse) {
      dialogData.originalValue = this.license.registeredNurseName;
    }
    else if (entityType === this.sharedService.parent) {
      dialogData.originalValue = this.license.parentAgency
      dialogData.newValue = this.license.entityName;
    }
    else if (entityType === this.sharedService.address) {
      dialogData.originalValue = this.addresses.getPhysicalAddress(this.addresses.addresses);
    }

    this.sharedService.requestChange(dialogData);
  }

  viewAuditLog(): void {
    this.sharedService.openAuditLog(this.license.id, this.sharedService.inHomeCareLicense);
  }

  getParentInfo(getCity: boolean): void {
    this.licenseService.getParentInfo(this.license.parentAgency!).subscribe({
      next: response => {
        this.parentId = response.id;
        this.license.parentAgency = response.entityName;
        this.license.parentGuid = response.licenseId;
      },
      error: e => {
        console.log('error getting parent info', e);
        this.sharedService.toastr.error('Error getting parent info');
      },
      complete: () => {
        this.subunitLoaded = true;
        if (getCity) {
          this.getParentCity();
        }
      }
    });
  }

  getParentCity(): void {
    this.addresses.addressService.getAddresses(this.parentId, this.sharedService.inHomeCareLicense, false).subscribe({
      next: response => {
        let parentPhysicalAddress: IAddress = this.addresses.getPhysicalAddress(response);
        this.license.parentCity = parentPhysicalAddress.city;
      },
      error: e => {
        console.log('error getting parent city', e);
        this.sharedService.toastr.error('Error getting parent city');
      },
      complete: () => {
        this.saveLicense();
      }
    });
  }

  hasPhysicalAddress(): boolean {
    const physicalAddress = this.addresses?.getPhysicalAddress(this.addresses.addresses);
    return !!physicalAddress && physicalAddress.id > 0;
  }

  openPDFDialog(license: IInHomeCareLicense): void {
    this.dialog.open(ExportInHomeCareLicenseDialogComponent, {
      data: license,
      role: 'dialog',
      ariaLabel: "License PDF Dialog",
      minWidth: !this.sharedService.mobile ? '800px' : '',
      maxWidth: !this.sharedService.mobile ? '1000px' : '300px'
    });
  }

  addressesLoadedEvent(loaded: boolean): void {
    this.addressesLoaded = loaded;
  }

  paymentsLoadedEvent(loaded: boolean): void {
    this.paymentsLoaded = loaded;
  }

  changeApproved(entity: string): void {
    switch (entity) {
      case this.sharedService.administratorApplication:
        this.adminApplication.getApplication();
        break;
      case this.sharedService.branchOperation:
        this.branchOperations.getOperations(false);
        break
      case this.sharedService.entityOwner:
        this.owners.getOwners(false);
        break;
      case this.sharedService.address:
        this.addresses.getAddresses(false);
        break;
    }
  }

  public get locked(): boolean {
    return !this.license.statuses.some(s => s.label === this.sharedService.started);
  }

  public get approved(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.approved);
  }

  public get canApprove(): boolean {
    let form = this.licenseForm.value;
    if (!form.backgroundCheckCompleted || form.backgroundCheckDate === null || !this.paid || this.approved) {
      return false;
    }
    if (!form.initialSurveyComplete && !this.isRenewal) {
      return false;
    }

    if (this.owners?.owners) {
      for (const owner of this.owners.owners) {
        if (owner.contactWithClient && (!owner.backgroundCheckCompleted || owner.backgroundCheckDate === null)) {
          return false;
        }
      }
    }

    return true;
  }

  public get isRenewal(): boolean {
    return this.license.statuses.some(s => s.label === this.sharedService.renewal) || this.renewal;
  }

  public get paid(): boolean {
    let payments: IPayment[] = this.payments?.payments;
    if (!payments || payments.length === 0) {
      return false
    }
    if (this.isRenewal) {
      return !payments.some(p => p.type === this.payments.paymentService.renewalFeeType && !p.paid);
    }
    return !payments.some(p => p.type === this.payments.paymentService.applicationFeeType && !p.paid);
  }

  public get canEditApprovedDate(): boolean {
    return this.authService.isOPA3 ||
      this.authService.isProgramManager ||
      this.authService.isSectionManager ||
      this.authService.isSectionAdministrator;
  }

  validateApplication(): void {
    this.validationErrors = [];

    // Only run validations if a license is selected
    if (this.license.id === 0) {
      return;
    }

    // Check all form validators and add messages for each invalid validator
    Object.keys(this.licenseForm.controls).forEach(key => {
      const control = this.licenseForm.get(key);
      if (control && control.invalid && control.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          switch (key) {
            case 'classificationLevel':
              if (errorKey === 'required') {
                this.validationErrors.push('Classification level is required');
              }
              break;
            case 'agencyDBAName':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency DBA name is required');
              }
              break;
            case 'agencyPhone':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency phone number is required');
              } else if (errorKey === 'pattern') {
                this.validationErrors.push('Agency phone number format is invalid');
              }
              break;
            case 'agencyEmail':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency email is required');
              } else if (errorKey === 'email') {
                this.validationErrors.push('Agency email format is invalid');
              }
              break;
            case 'agencyFax':
              if (errorKey === 'pattern') {
                this.validationErrors.push('Agency fax number format is invalid');
              }
              break;
            case 'emergencyContactName':
              if (errorKey === 'required') {
                this.validationErrors.push('Emergency contact name is required');
              }
              break;
            case 'emergencyContactPhone':
              if (errorKey === 'required') {
                this.validationErrors.push('Emergency contact phone number is required');
              } else if (errorKey === 'pattern') {
                this.validationErrors.push('Emergency contact phone number format is invalid');
              }
              break;
            case 'emergencyContactEmail':
              if (errorKey === 'required') {
                this.validationErrors.push('Emergency contact email is required');
              }
              break;
            case 'agencyPhysicalLocation':
              if (errorKey === 'required') {
                this.validationErrors.push('Agency physical location is required');
              }
              break;
            case 'adminDesigneeName':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrative designee name is required');
              }
              break;
            case 'adminDesigneeTitle':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrative designee title is required');
              }
              break;
            case 'qualifiedEntityName':
              if (errorKey === 'required') {
                this.validationErrors.push('Qualified entity name is required');
              }
              break;
            case 'qualifiedEntityTitle':
              if (errorKey === 'required') {
                this.validationErrors.push('Qualified entity title is required');
              }
              break;
            case 'entityName':
              if (errorKey === 'agencyNameRequired') {
                this.validationErrors.push('Agency name is required and cannot contain "home health"');
              }
              break;
            case 'entityWebsite':
              if (errorKey === 'pattern') {
                this.validationErrors.push('Entity website URL format is invalid');
              }
              break;
            case 'parentAgency':
              if (errorKey === 'invalidParent') {
                this.validationErrors.push('Parent agency is invalid or not found');
              }
              break;
          }
        });
      }
    });

    // Check form-level validators
    if (this.licenseForm.errors) {
      Object.keys(this.licenseForm.errors).forEach(errorKey => {
        switch (errorKey) {
          case 'rnRequired':
            this.validationErrors.push('Registered nurse name is required for Intermediate and Comprehensive classifications');
            break;
          case 'otherLocationRequired':
            this.validationErrors.push('Other location description is required when "Other Licensed Facility or Agency Type" is selected');
            break;
        }
      });
    }

    // Validate branch operations
    if (this.branchOperations) {
      this.validationErrors.push(...this.branchOperations.validateAddresses());
    }

    // Validate office hours
    if (this.officeHours && this.officeHours.officeHoursForm && this.officeHours.officeHoursForm.invalid) {
      this.validationErrors.push('Missing agency office hours information');
    }

    // Validate main agency address (similar to public side)
    if (this.addresses && !this.hasPhysicalAddress()) {
      this.validationErrors.push('Missing agency physical address');
    }

    // Check approval workflow
    if (this.license.primaryApproved === null && this.license.primaryApproverName) {
      this.validationErrors.push('Primary approval decision is required');
    }

    if (this.license.secondaryApproved === null && this.license.secondaryApproverName) {
      this.validationErrors.push('Secondary approval decision is required');
    }

    // Check for required documents at agency level
    let agencyTypes: string[] = [];
    this.documents.toArray().forEach((document) => {
      let documentSet = document.documents;
      documentSet.forEach((doc) => {
        doc.types.forEach((type) => {
          agencyTypes.push(type.type);
        });
      });
    });

    if (!agencyTypes.includes('IHC Policies, Procedures and Forms')) {
      this.validationErrors.push('Missing IHC Policies, Procedures and Forms');
    }

    if (!agencyTypes.includes('Policy and Procedure Checklist')) {
      this.validationErrors.push('Missing Policy and Procedure Checklist documentation');
    }

    if (this.license.agencyPhysicalLocation === 'Private Home Residence' && !agencyTypes.includes('Proof of Business Zoning Approval or Allowance')) {
      this.validationErrors.push('Missing agency proof of business zoning approval or allowance');
    }

    this.validateOwners();
    this.validateAdministratorApplication();
  }

  validateOwners(): void {
    if (!this.owners) {
      this.validationErrors.push('Owner information is not available');
      return;
    }

    // Validate owners
    if (this.owners.owners.length === 0) {
      this.validationErrors.push('No owner information provided');
    }

    // Use individual validation methods that exist in staff side
    if (this.owners.validateAddresses) {
      this.validationErrors.push(...this.owners.validateAddresses());
    }
    if (this.owners.validateDocuments) {
      this.validationErrors.push(...this.owners.validateDocuments());
    }
  }

  validateAdministratorApplication(): void {
    if (!this.adminApplication || this.adminApplication.needApplication) {
      this.validationErrors.push('Missing administrator information');
      return;
    }

    // Validate administrator application using enhanced logic
    Object.keys(this.adminApplication.applicationForm.controls).forEach(key => {
      const control = this.adminApplication.applicationForm.get(key);
      if (control && control.invalid && control.errors) {
        Object.keys(control.errors).forEach(errorKey => {
          switch (key) {
            case 'firstName':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator first name is required');
              }
              break;
            case 'lastName':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator last name is required');
              }
              break;
            case 'phone':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator phone number is required');
              } else if (errorKey === 'pattern') {
                this.validationErrors.push('Administrator phone number format is invalid');
              }
              break;
            case 'email':
              if (errorKey === 'required') {
                this.validationErrors.push('Administrator email is required');
              } else if (errorKey === 'email') {
                this.validationErrors.push('Administrator email format is invalid');
              }
              break;
          }
        });
      }
    });

    let rawForm = this.adminApplication.applicationForm.getRawValue();
    if (rawForm.hasProfessionalExperience === null) {
      this.validationErrors.push('Administrator professional experience selection is required');
    }
    if (rawForm.hasManagementExperience === null) {
      this.validationErrors.push('Administrator management experience selection is required');
    }

    // Check administrator form-level validators
    if (this.licenseForm.errors) {
      Object.keys(this.licenseForm.errors).forEach(errorKey => {
        switch (errorKey) {
          case 'HSDateRangeInvalid':
            this.validationErrors.push('High school to date is before from date');
            break;
          case 'CollegeDateRangeInvalid':
            this.validationErrors.push('College to date is before from date');
            break;
          case 'OtherDateRangeInvalid':
            this.validationErrors.push('Other education to date is before from date');
            break;
        }
      });
    }

    // Check administrator-specific business rules like public side
    const administratorAddress = this.adminApplication.addresses?.getPhysicalAddress(this.adminApplication.addresses.addresses);
    if (!administratorAddress || administratorAddress.id === 0) {
      this.validationErrors.push('Missing administrator physical address');
    }

    // Check document requirements
    let types: string[] = [];
    (this.adminApplication.documents?.documents ?? []).forEach((document) => {
      document.types.forEach((type) => {
        types.push(type.type);
      });
    });

    if (!types.includes('Resume')) {
      this.validationErrors.push('Missing administrator resume');
    }

    if (!types.includes('High School Diploma or Equivalent')) {
      this.validationErrors.push('Missing administrator proof of high school diploma or equivalent');
    }

    if (this.adminApplication.applicationForm.get('contactWithClient')?.value && !types.includes('Background Check Request')) {
      this.validationErrors.push('Missing administrator background check request');
    }

    if (!this.adminApplication.applicationForm.get('contactWithClient')?.value && !types.includes('No Client Contact Plan')) {
      this.validationErrors.push('Missing administrator no client contact plan');
    }

    if (this.adminApplication.applicationForm.get('hasProfessionalExperience')?.value && !types.includes('Proof of Credential')) {
      this.validationErrors.push('Missing administrator proof of credentials for professional experience');
    }

    // Check experience requirements
    if (this.adminApplication.applicationForm.get('hasManagementExperience')?.value) {
      if (!this.adminApplication.managementExperience?.experiences.length) {
        this.validationErrors.push('Missing administrator management experience information');
      }
    }

    if (this.adminApplication.applicationForm.get('hasProfessionalExperience')?.value) {
      if (!this.adminApplication.professionalExperience?.experiences.length) {
        this.validationErrors.push('Missing administrator professional experience information');
      }
    }
  }

  //Custom Validators
  registeredNurseValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const classification = control.value.classificationLevel;
      const registeredNurse = control.value.registeredNurseName;

      if (classification !== 'Intermediate' && classification !== 'Comprehensive') {
        return null;
      }
      return registeredNurse ? null : { rnRequired: true };
    }
  }

  validateRegisteredNurse(): boolean {
    if (this.licenseForm.hasError('rnRequired') && this.licenseForm.get('registeredNurseName')?.touched) {
      this.licenseForm.get('registeredNurseName')?.setErrors(['required']);
      return true;
    }
    return false;
  }

  physicalLocationValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const agencyPhysicalLocation = control.value.agencyPhysicalLocation;
      const otherLocation = control.value.otherLocation;

      if (agencyPhysicalLocation !== 'other') {
        return null;
      }
      return otherLocation ? null : { otherLocationRequired: true };
    }
  }

  validatePhysicalLocation(): boolean {
    if (this.licenseForm.hasError('otherLocationRequired') && this.licenseForm.get('otherLocation')?.touched) {
      this.licenseForm.get('otherLocation')?.setErrors(['required']);
      return true;
    }
    return false;
  }
}
